import * as Color from 'color';

import { DEFAULT_COLOR_CONTRAST_VALUES } from './constants';
import type { ContrastColorValues } from './types';

// Originally taken from: https://css-tricks.com/converting-color-spaces-in-javascript/#hex-to-hsl

/** Convert a 4 or 7 length hex string to HSL color space.
 * Invalid lengths will result in a black HSL color output. */
export function hexToHsl(H: string): Record<'h' | 's' | 'l', number> {
  // Convert hex to RGB first
  let r = '0';
  let g = '0';
  let b = '0';
  if (H.length === 4) {
    r = '0x' + H[1] + H[1];
    g = '0x' + H[2] + H[2];
    b = '0x' + H[3] + H[3];
  } else if (H.length === 7) {
    r = '0x' + H[1] + H[2];
    g = '0x' + H[3] + H[4];
    b = '0x' + H[5] + H[6];
  }
  // Then to HSL
  const rToHsl = Number(r) / 255,
    gToHsl = Number(g) / 255,
    bToHsl = Number(b) / 255;

  const cmin = Math.min(rToHsl, gToHsl, bToHsl);
  const cmax = Math.max(rToHsl, gToHsl, bToHsl);
  const delta = cmax - cmin;

  let h = 0;
  let s = 0;
  let l = 0;

  if (delta === 0) {
    h = 0;
  } else if (cmax === rToHsl) {
    h = ((gToHsl - bToHsl) / delta) % 6;
  } else if (cmax === gToHsl) {
    h = (bToHsl - rToHsl) / delta + 2;
  } else {
    h = (rToHsl - gToHsl) / delta + 4;
  }

  h = Math.round(h * 60);

  if (h < 0) {
    h += 360;
  }

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return { h, s, l };
}

// Taken from: https://stackoverflow.com/a/44134328
export function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0'); // convert to Hex and prefix "0" if needed
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**Convert from hex to rgb color value. Returns either **rgb** or
 * **rgba** (if a valid second argument is provided for alpha).
 *
 * Currently presupposes a 6-decimal hex specification.
 */
export const hexToRgb = (hexString: string, alpha?: number): string => {
  if (hexString[0] !== '#') return '';
  const r = parseInt(hexString.substring(1, 3), 16);
  const g = parseInt(hexString.substring(3, 5), 16);
  const b = parseInt(hexString.substring(5, 7), 16);
  const rgb = `${r}, ${g}, ${b}`;
  if (alpha && Number(alpha)) {
    return `rgba(${rgb}, ${alpha})`;
  }
  return `rgb(${rgb})`;
};

/** Handles rgb and rgba colors. */
export const rgbToHex = (rgb: string): string => {
  if (rgb[3] === 'a') {
    return Color(rgb).hexa();
  }
  return Color(rgb).hex();
};

export const getColorContrast = (color: string, contrastingColor: string) =>
  Color(color).contrast(Color(contrastingColor));

export const contrastColor = (
  color: string,
  contrastValues?: Partial<ContrastColorValues>,
): string => {
  if (color === 'inherit' || color === 'transparent') {
    return 'inherit';
  }
  const {
    lowContrastReturn,
    highContrastReturn,
    contrastRatio,
    contrastingColor,
  } = { ...DEFAULT_COLOR_CONTRAST_VALUES, ...contrastValues };

  const contrast = getColorContrast(color, contrastingColor);
  return contrast > contrastRatio ? highContrastReturn : lowContrastReturn;
};

/** If the passed color  satisfies contrast requirements against
 * a white contrasting color, then the passed color will be returned.
 * Otherwise, black is returned. Custom contrast values can be passed. */
export const validateColorContrast = (
  color: string,
  contrastValues?: Partial<ContrastColorValues>,
) =>
  contrastColor(color, {
    // This needs to be changed if the host will change
    // background color theme based on user preferences
    contrastingColor: '#FFFFFF',
    lowContrastReturn: '#000000',
    highContrastReturn: color,
    ...contrastValues,
  });

export function getColorStates(
  colorVal: string,
  contrastValues?: Partial<ContrastColorValues>,
): Record<'baseColor' | 'focusColor', string> {
  let focusColor: string;
  let baseColor: string;
  try {
    const color = validateColorContrast(colorVal, contrastValues);
    focusColor = Color(color).alpha(0.2).toString();
    baseColor = Color(color).toString();
  } catch {
    baseColor = '#000000';
    focusColor = 'rgba(0, 0, 0, 0.2)';
  }
  return { baseColor, focusColor };
}

export const calculateAverageRGB = (
  imgData: string,
  outputFormat: 'rgb' | 'hex' = 'hex',
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas'),
      context = canvas.getContext && canvas.getContext('2d'),
      rgb = { r: 0, g: 0, b: 0 };

    let width: number,
      height: number,
      i = 0,
      length: number,
      count = 0;

    const imgEl = new Image();
    imgEl.src = imgData;

    imgEl.onerror = () => {
      reject(new Error(`Failed to load image: ${imgData}`));
    };

    imgEl.onload = () => {
      height = canvas.height =
        imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height;
      width = canvas.width =
        imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width;

      if (context) {
        context.drawImage(imgEl, 0, 0, width, height);

        const data = context.getImageData(0, 0, width, height);

        length = data.data.length;

        while ((i += 1 * 4) < length) {
          ++count;
          rgb.r += data.data[i];
          rgb.g += data.data[i + 1];
          rgb.b += data.data[i + 2];
        }

        rgb.r = Math.floor(rgb.r / count);
        rgb.g = Math.floor(rgb.g / count);
        rgb.b = Math.floor(rgb.b / count);

        const rgbString = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
        resolve(outputFormat === 'rgb' ? rgbString : rgbToHex(rgbString));
      }
    };
  });
};
